import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBuilding,
  faArrowsSpin,
  faPhoneVolume,
  faCircleCheck,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons"
import {
  trackingStatusOptions,
  csatStatusOptions,
  statusMeta,
  toneDot,
} from "../statusConfig"

const formatCount = (count) => (Number.isFinite(count) ? count : "—")

const kpiAccents = {
  indigo: { chip: "bg-indigo-500/10 text-indigo-600", bar: "bg-indigo-500" },
  blue: {
    chip: "bg-secondary/10 text-secondary_dark",
    bar: "bg-secondary_dark",
  },
  teal: { chip: "bg-teal-500/10 text-teal-600", bar: "bg-teal-500" },
  amber: { chip: "bg-amber-500/10 text-amber-600", bar: "bg-amber-500" },
  emerald: {
    chip: "bg-emerald-500/10 text-emerald-600",
    bar: "bg-emerald-600",
  },
}

const KpiTile = ({
  label,
  count,
  sublabel,
  sublabelTitle,
  isLoading,
  isError,
  icon,
  accent,
}) => (
  <div className="relative flex flex-1 min-w-[220px] items-center gap-3.5 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <span
      className={`absolute inset-y-0 left-0 w-1 ${kpiAccents[accent].bar}`}
    />
    <div
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${kpiAccents[accent].chip}`}
    >
      <FontAwesomeIcon className="h-4.5 w-4.5" icon={icon} />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-gray-700">
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-bold leading-none tabular-nums text-gray-900">
        {isLoading ? (
          <span className="inline-block h-6 w-12 animate-pulse rounded bg-gray-100 align-middle" />
        ) : isError ? (
          "—"
        ) : (
          formatCount(count)
        )}
      </div>
      {sublabel && !isLoading && !isError && (
        <div
          className="mt-1 truncate text-xs text-gray-400"
          title={
            sublabelTitle ??
            (typeof sublabel === "string" ? sublabel : undefined)
          }
        >
          {sublabel}
        </div>
      )}
    </div>
  </div>
)

const pluralizeStudies = (count) =>
  `${count} ${count === 1 ? "study" : "studies"}`

// One row of the pipeline bar — index, label + tone dot, a track/fill bar
// sized relative to the largest count across ALL 9 stages (so bar lengths
// are comparable pipeline-wide, not just within their own group), and the
// raw count on the right.
const StageBar = ({
  index,
  label,
  count,
  isLoading,
  isError,
  tone,
  maxCount,
  milestone,
}) => {
  const widthPct =
    isLoading || isError || !Number.isFinite(count) || !maxCount
      ? 0
      : Math.max((count / maxCount) * 100, count > 0 ? 3 : 0)

  return (
    <div className="grid grid-cols-[18px_minmax(140px,200px)_1fr_32px] items-center gap-2 py-1">
      <div className="text-center text-xs font-semibold tabular-nums text-gray-400">
        {index}
      </div>
      <div
        className={`flex items-center gap-1.5 truncate text-[13px] ${milestone ? "font-semibold text-gray-900" : "text-gray-600"}`}
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${toneDot[tone]}`} />
        <span className="truncate">{label}</span>
      </div>
      <div className="h-5 overflow-hidden rounded-md bg-gray-100">
        {!isLoading && !isError && (
          <div
            className={`h-full rounded-md transition-all duration-700 ${toneDot[tone]}`}
            style={{ width: `${widthPct}%` }}
          />
        )}
      </div>
      <div className="text-right text-sm font-bold tabular-nums text-gray-900">
        {isLoading ? (
          <span className="inline-block h-4 w-6 animate-pulse rounded bg-gray-100" />
        ) : isError ? (
          "—"
        ) : (
          formatCount(count)
        )}
      </div>
    </div>
  )
}

const GroupTag = ({ children, tone }) => (
  <span
    className={`rounded px-2 py-0.5 text-[11px] font-semibold text-white ${toneDot[tone]}`}
  >
    {children}
  </span>
)

const sumCounts = (counts, statuses) => {
  const values = statuses.map((status) => counts[status]?.count)
  return values.every(Number.isFinite)
    ? values.reduce((sum, value) => sum + value, 0)
    : undefined
}

// Each tile is one slice of the pipeline by stage. All but Receipt
// Confirmation Pending count centers with at least one study in their
// stages (so a center can appear in several tiles); that one counts studies.
const OverviewStats = ({ grandTotal, trackingCounts, csatCounts, centers }) => {
  const caseInProgressCases = sumCounts(trackingCounts, [
    "NEW_CENTER_CASE_RECEIVED",
    "CASE_VERIFICATION",
    "CASE_ASSIGNED_IN_PROGRESS",
  ])
  const ceoDueCases = csatCounts.CEO_OUTREACH_PENDING?.count
  // Studies one stage short of each confirmation: still at Receipt
  // Confirmation Pending are waiting on the imaging center; already at
  // Imaging Center Confirmed are waiting on the referring doctor.
  const pendingImagingCenter = csatCounts.RECEIPT_CONFIRMATION_PENDING?.count
  const pendingReferringDoctor = csatCounts.IMAGING_CENTER_CONFIRMED?.count
  const confirmationCases = sumCounts(csatCounts, [
    "RECEIPT_CONFIRMATION_PENDING",
    "IMAGING_CENTER_CONFIRMED",
    "REFERRING_DOCTOR_CONFIRMED",
  ])

  const allCounts = [
    ...trackingStatusOptions.map(({ value }) => trackingCounts[value]),
    ...csatStatusOptions.map(({ value }) => csatCounts[value]),
  ]
  const maxCount = Math.max(
    1,
    ...allCounts.filter((c) => Number.isFinite(c?.count)).map((c) => c.count)
  )

  return (
    <div className="mb-5 flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <KpiTile
          accent="indigo"
          count={centers.total}
          icon={faBuilding}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="Tracked"
          sublabel={
            Number.isFinite(grandTotal.count)
              ? pluralizeStudies(grandTotal.count)
              : undefined
          }
        />
        <KpiTile
          accent="blue"
          count={centers.caseInProgressCenters}
          icon={faArrowsSpin}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="In Progress"
          sublabel={
            Number.isFinite(caseInProgressCases)
              ? `${pluralizeStudies(caseInProgressCases)} in progress`
              : undefined
          }
        />
        <KpiTile
          accent="teal"
          count={confirmationCases}
          icon={faClipboardCheck}
          isError={csatCounts.RECEIPT_CONFIRMATION_PENDING?.isError}
          isLoading={csatCounts.RECEIPT_CONFIRMATION_PENDING?.isLoading}
          label="Receipt Confirmation Pending"
          sublabel={
            Number.isFinite(confirmationCases) ? (
              <>
                Pending: Imaging ctr{" "}
                <span className="font-bold tabular-nums text-gray-700">
                  {pendingImagingCenter}
                </span>
                <span className="mx-1.5 text-gray-300">|</span>
                Ref. doctor{" "}
                <span className="font-bold tabular-nums text-gray-700">
                  {pendingReferringDoctor}
                </span>
              </>
            ) : undefined
          }
          sublabelTitle={
            Number.isFinite(confirmationCases)
              ? `${pendingImagingCenter} Pending Imaging Center Confirmation, ${pendingReferringDoctor} Pending Referring Doctor Confirmation`
              : undefined
          }
        />
        <KpiTile
          accent="amber"
          count={centers.ceoOutreachDueCenters}
          icon={faPhoneVolume}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="Awaiting CEO Call"
          sublabel={
            Number.isFinite(ceoDueCases)
              ? `${pluralizeStudies(ceoDueCases)} waiting`
              : undefined
          }
        />
        <KpiTile
          accent="emerald"
          count={centers.ceoOutreachCompleteCenters}
          icon={faCircleCheck}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="Completed"
          sublabel="all tracked studies closed out"
        />
      </div>

      {/*Todo: Hiding this overview stats for now as discussed */}

      {/* <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Where every tracked case sits
          </h2>
          <p className="mt-0.5 text-xs text-gray-400">
            One pipeline, two deals. The case deal runs stages 1–4; when the
            report is signed, a report-confirmation deal is created at stage 5
            and runs to 9.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <GroupTag tone="blue">Case deal</GroupTag>
              <span className="text-xs font-semibold text-gray-900">
                Stages 1–4
              </span>
            </div>
            <div>
              {trackingStatusOptions.map(({ value, label }, i) => (
                <StageBar
                  key={value}
                  count={trackingCounts[value]?.count}
                  index={i + 1}
                  isError={trackingCounts[value]?.isError}
                  isLoading={trackingCounts[value]?.isLoading}
                  label={label}
                  maxCount={maxCount}
                  milestone={value === "REPORT_SIGNED"}
                  tone={statusMeta[value].tone}
                />
              ))}
            </div>
          </div>

          <div className="hidden w-px self-stretch bg-gray-200 md:block" />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <GroupTag tone="emerald">Report confirmation deal</GroupTag>
              <span className="text-xs font-semibold text-gray-900">
                Stages 5–9
              </span>
            </div>
            <div>
              {csatStatusOptions.map(({ value, label }, i) => (
                <StageBar
                  key={value}
                  count={csatCounts[value]?.count}
                  index={i + 5}
                  isError={csatCounts[value]?.isError}
                  isLoading={csatCounts[value]?.isLoading}
                  label={label}
                  maxCount={maxCount}
                  tone={statusMeta[value].tone}
                />
              ))}
            </div>
          </div>
        </div>
      </div> */}
    </div>
  )
}

export default OverviewStats
